import React, { useEffect, useMemo, useState } from "react";
import { L as Link } from "../lib/i18n";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { CH_CENTER, geocodeLocation } from "../lib/swissGeo";
import { fmtCHF, fmtKM } from "../lib/constants";
import { useLang } from "../contexts/LanguageContext";

// Custom red icon (avoids broken default-marker URL in webpack builds)
const carIcon = new L.DivIcon({
  className: "",
  html: `<div style="background:#dc2626;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 12px rgba(220,38,38,0.45);display:grid;place-items:center;border:2px solid #fff"><div style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:13px">🚗</div></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -24],
});

export default function MapView() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listListings({ limit: 500 }).then((d) => setItems(d.items)).finally(() => setLoading(false));
  }, []);

  const points = useMemo(() => {
    return items
      .map((c) => ({ ...c, _coord: geocodeLocation(c.location) }))
      .filter((c) => c._coord);
  }, [items]);

  const unmapped = items.length - points.length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">Map</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">{t.nav.map || "Karte"}</h1>
          <p className="mt-2 text-sm text-zinc-400">
            {points.length} {points.length === 1 ? "Inserat" : "Inserate"} auf der Karte
            {unmapped > 0 && <span className="text-zinc-500"> · {unmapped} ohne Standort</span>}
          </p>
        </div>
        <Link to="/listings" className="text-sm text-zinc-400 hover:text-white">← Listenansicht</Link>
      </div>

      <div className="mt-8 overflow-hidden rounded-lg border border-zinc-800" data-testid="map-container">
        {loading ? (
          <div className="h-[640px] animate-pulse bg-zinc-950" />
        ) : (
          <MapContainer center={CH_CENTER} zoom={8} style={{ height: 640, width: "100%", background: "#000" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {points.map((c) => (
              <Marker key={c.id} position={c._coord} icon={carIcon}>
                <Popup>
                  <div style={{ width: 220 }}>
                    {c.images?.[0] && (
                      <img
                        src={api.fileUrl(c.images[0])}
                        alt=""
                        style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 6 }}
                      />
                    )}
                    <div style={{ marginTop: 8, fontWeight: 600, fontSize: 14 }}>{c.title}</div>
                    <div style={{ fontSize: 12, color: "#71717a", marginTop: 2 }}>
                      {c.year} · {fmtKM(c.mileage_km)} km · {c.location}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#dc2626", marginTop: 6 }}>{fmtCHF(c.price_chf)}</div>
                    <Link
                      to={`/listings/${c.id}`}
                      style={{ display: "inline-block", marginTop: 10, background: "#dc2626", color: "#fff", padding: "6px 12px", borderRadius: 4, textDecoration: "none", fontSize: 13 }}
                    >
                      Details ansehen
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      <p className="mt-4 text-xs text-zinc-500">
        Standorte werden anhand der Stadt im Inserat zugeordnet (für Schweizer Städte). Andere Orte werden vorerst nicht angezeigt.
      </p>
    </div>
  );
}
